import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import NCFManagement from './NCFManagement';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Zap, FileText, CheckCircle2, XCircle, Eye, EyeOff, Loader2 } from 'lucide-react';

const TIPOS_FACTURACION = [
  { value: 'ninguna', label: '1 - Ninguna', hint: 'Facturas sin NCF' },
  { value: 'tradicional', label: '2 - Tradicional', hint: 'B01, B02, B14, B15' },
  { value: 'electronica', label: '3 - Electrónica', hint: 'E31, E32, E44, E45' },
];

const ComprobantesFiscales = ({ user }) => {
  const isSuperadmin = user?.role === 'superadmin';
  const [tab, setTab] = useState(isSuperadmin ? 'api' : 'ranges');

  // Estado de la configuración API e-CF
  const [ecfConfig, setEcfConfig] = useState({
    tipo_facturacion: 'tradicional',
    ecf_api_url: '',
    ecf_api_email: '',
    ecf_api_password: '',
    ecf_api_rnc: '',
    ecf_api_password_saved: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isSuperadmin) {
      fetchEcfConfig();
    }
  }, [isSuperadmin]);

  const fetchEcfConfig = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/business-config/ecf`);
      setEcfConfig({
        tipo_facturacion: resp.data.tipo_facturacion || 'tradicional',
        ecf_api_url: resp.data.ecf_api_url || '',
        ecf_api_email: resp.data.ecf_api_email || '',
        ecf_api_password: '',
        ecf_api_rnc: resp.data.ecf_api_rnc || '',
        ecf_api_password_saved: resp.data.ecf_api_password_saved || false,
      });
    } catch (e) {
      toast.error('Error cargando configuración de e-CF');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        tipo_facturacion: ecfConfig.tipo_facturacion,
        ecf_api_url: ecfConfig.ecf_api_url.trim(),
        ecf_api_email: ecfConfig.ecf_api_email.trim(),
        ecf_api_rnc: ecfConfig.ecf_api_rnc.trim(),
      };
      // Solo enviar la contraseña si el usuario la escribió (si está vacía, mantener la actual)
      if (ecfConfig.ecf_api_password) {
        payload.ecf_api_password = ecfConfig.ecf_api_password;
      }
      const resp = await axios.put(`${API}/business-config/ecf`, payload);
      toast.success('Configuración de e-CF guardada');
      setEcfConfig(prev => ({
        ...prev,
        ecf_api_password: '',
        ecf_api_password_saved: resp.data.ecf_api_password_saved,
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = {};
      if (ecfConfig.ecf_api_url) payload.api_url = ecfConfig.ecf_api_url.trim();
      if (ecfConfig.ecf_api_email) payload.email = ecfConfig.ecf_api_email.trim();
      if (ecfConfig.ecf_api_password) payload.password = ecfConfig.ecf_api_password;

      const resp = await axios.post(`${API}/business-config/ecf/test-connection`, payload);
      setTestResult(resp.data);
      if (resp.data.success) {
        toast.success('Conexión exitosa');
      } else {
        toast.error(resp.data.message || 'Error de autenticación');
      }
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || err.message });
      toast.error(err.response?.data?.detail || 'Error probando conexión');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="comprobantes-fiscales-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2">
          <FileText className="text-emerald-600" size={28} />
          Comprobantes Fiscales
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Gestión de NCF tradicionales (B01-B15) y Facturación Electrónica (e-CF: E31-E45).
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="w-full md:w-auto">
          {isSuperadmin && (
            <TabsTrigger value="api" data-testid="tab-api-config">
              <Zap size={16} className="mr-2" /> Configuración API e-CF
            </TabsTrigger>
          )}
          <TabsTrigger value="ranges" data-testid="tab-ranges">
            <FileText size={16} className="mr-2" /> Rangos de Comprobantes
          </TabsTrigger>
        </TabsList>

        {isSuperadmin && (
          <TabsContent value="api" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Credenciales del Proveedor de Facturación Electrónica</CardTitle>
                <CardDescription>
                  Esta configuración es solo visible para el SuperAdmin. Guarda la URL del API,
                  usuario y contraseña del proveedor (ej: Verifact). Los demás usuarios no verán
                  esta sección.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="animate-spin text-emerald-500" size={24} />
                  </div>
                ) : (
                  <form onSubmit={handleSave} className="space-y-4">
                    <div className="rounded-lg border p-3 bg-slate-50 space-y-3">
                      <div>
                        <Label className="font-semibold">Tipo de Facturación</Label>
                        <p className="text-xs text-slate-500">
                          Define qué comprobantes se usan en POS, Facturación, Clientes y Cotizaciones.
                        </p>
                      </div>
                      <RadioGroup
                        value={ecfConfig.tipo_facturacion}
                        onValueChange={(v) => setEcfConfig(prev => ({ ...prev, tipo_facturacion: v }))}
                        className="grid grid-cols-1 md:grid-cols-3 gap-2"
                        data-testid="tipo-facturacion-radio"
                      >
                        {TIPOS_FACTURACION.map(t => (
                          <label
                            key={t.value}
                            htmlFor={`tf-${t.value}`}
                            className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer bg-white transition-colors ${ecfConfig.tipo_facturacion === t.value ? 'border-emerald-500 ring-1 ring-emerald-500' : 'border-slate-200 hover:border-slate-300'}`}
                          >
                            <RadioGroupItem id={`tf-${t.value}`} value={t.value} data-testid={`tipo-facturacion-${t.value}`} className="mt-0.5" />
                            <span>
                              <span className="block text-sm font-semibold text-slate-800">{t.label}</span>
                              <span className="block text-xs text-slate-500">{t.hint}</span>
                            </span>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ecf_api_url">URL del API *</Label>
                      <Input
                        id="ecf_api_url"
                        placeholder="https://ecf-test.api.verifact.com.do/api"
                        value={ecfConfig.ecf_api_url}
                        onChange={(e) => setEcfConfig(prev => ({ ...prev, ecf_api_url: e.target.value }))}
                        data-testid="ecf-api-url-input"
                      />
                      <p className="text-xs text-slate-400">Sin /auth/login al final; lo agregamos automáticamente.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="ecf_api_email">Usuario / Email *</Label>
                        <Input
                          id="ecf_api_email"
                          type="email"
                          placeholder="admin@tu-empresa.com"
                          value={ecfConfig.ecf_api_email}
                          onChange={(e) => setEcfConfig(prev => ({ ...prev, ecf_api_email: e.target.value }))}
                          data-testid="ecf-api-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ecf_api_password">
                          Contraseña *
                          {ecfConfig.ecf_api_password_saved && !ecfConfig.ecf_api_password && (
                            <span className="ml-2 text-xs text-emerald-600">(guardada — déjala vacía para no cambiarla)</span>
                          )}
                        </Label>
                        <div className="relative">
                          <Input
                            id="ecf_api_password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder={ecfConfig.ecf_api_password_saved ? '••••••••' : 'Contraseña'}
                            value={ecfConfig.ecf_api_password}
                            onChange={(e) => setEcfConfig(prev => ({ ...prev, ecf_api_password: e.target.value }))}
                            data-testid="ecf-api-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            tabIndex={-1}
                            data-testid="toggle-ecf-password-visibility"
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ecf_api_rnc">RNC del Emisor (opcional)</Label>
                      <Input
                        id="ecf_api_rnc"
                        placeholder="132062884"
                        value={ecfConfig.ecf_api_rnc}
                        onChange={(e) => setEcfConfig(prev => ({ ...prev, ecf_api_rnc: e.target.value }))}
                        data-testid="ecf-api-rnc-input"
                      />
                    </div>

                    <Separator />

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        type="submit"
                        className="bg-emerald-500 hover:bg-emerald-600"
                        disabled={saving}
                        data-testid="save-ecf-config-btn"
                      >
                        {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                        Guardar Configuración
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleTestConnection}
                        disabled={testing || !ecfConfig.ecf_api_url || !ecfConfig.ecf_api_email}
                        data-testid="test-ecf-connection-btn"
                      >
                        {testing ? <Loader2 className="animate-spin mr-2" size={16} /> : <Zap size={16} className="mr-2" />}
                        Probar Conexión
                      </Button>
                    </div>

                    {testResult && (
                      <div
                        className={`mt-3 rounded-lg border p-3 ${
                          testResult.success
                            ? 'bg-emerald-50 border-emerald-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                        data-testid="test-result-box"
                      >
                        <div className="flex items-center gap-2">
                          {testResult.success ? (
                            <CheckCircle2 className="text-emerald-600" size={18} />
                          ) : (
                            <XCircle className="text-red-600" size={18} />
                          )}
                          <span className={`font-semibold ${testResult.success ? 'text-emerald-800' : 'text-red-800'}`}>
                            {testResult.success ? 'Conexión exitosa' : 'Error'}
                          </span>
                        </div>
                        {testResult.message && (
                          <p className="text-sm text-slate-600 mt-1">{testResult.message}</p>
                        )}
                        {testResult.usuario && (
                          <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                            <div><strong>Usuario:</strong> {testResult.usuario.nombre} ({testResult.usuario.email})</div>
                            <div><strong>Rol:</strong> {testResult.usuario.rol}</div>
                            {testResult.usuario.tenant_nombre && (
                              <div><strong>Empresa:</strong> {testResult.usuario.tenant_nombre} — RNC {testResult.usuario.tenant_rnc}</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </form>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="ranges" className="mt-4">
          <NCFManagement user={user} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ComprobantesFiscales;
